import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Send, Trash2, ShieldAlert, Users2, Loader2, Smile } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMOJI = ['🎉', '💰', '🙌', '🔥', '🏆', '👏', '💪', '🤝', '✅', '🙏'];

/**
 * <ClubChat clubId currentUser clubCreatorId clubMembers />
 * Realtime per-club chat (WebSockets) with @mentions and auto-redact.
 */
export default function ClubChat({ clubId, currentUser, clubCreatorId, clubMembers = [] }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});  // {user_id: timestamp}
  const [connected, setConnected] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);

  const wsRef = useRef(null);
  const listRef = useRef(null);
  const stickToBottomRef = useRef(true);

  // ---------- WS connection lifecycle --------------------------------- //
  const connectWS = useCallback(() => {
    if (!currentUser) return;
    // Session cookie is HttpOnly — we cannot read it from JS. The browser
    // will attach it automatically on the WS Upgrade request thanks to
    // same-origin (or proper CORS withCredentials on the API).
    const wsProto = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const host = BACKEND_URL.replace(/^https?:\/\//, '');
    const url = `${wsProto}://${host}/api/ws/clubs/${clubId}/chat`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => { setConnected(false); wsRef.current = null; };
    ws.onerror = () => setConnected(false);
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'message') {
          setMessages((prev) => {
            if (prev.some((m) => m.message_id === data.message.message_id)) return prev;
            return [...prev, data.message];
          });
        } else if (data.type === 'delete') {
          setMessages((prev) =>
            prev.map((m) => (m.message_id === data.message_id ? { ...m, deleted: true } : m))
          );
        } else if (data.type === 'typing' && data.user_id !== currentUser.user_id) {
          setTypingUsers((prev) => ({ ...prev, [data.user_id]: Date.now() }));
        }
      } catch (e) {}
    };
    return ws;
  }, [clubId, currentUser]);

  // ---------- Initial load + WS connect ------------------------------- //
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/clubs/${clubId}/chat/messages`, {
          withCredentials: true,
        });
        if (mounted) setMessages(res.data.messages || []);
      } catch (e) {
        // Member-only — 403 is expected if user just joined and isn't synced yet.
      } finally {
        if (mounted) setLoading(false);
      }
      // Mark room as read
      try { await axios.post(`${BACKEND_URL}/api/clubs/${clubId}/chat/read`, {}, { withCredentials: true }); } catch (e) {}
    })();
    const ws = connectWS();
    return () => { mounted = false; if (ws) ws.close(); };
  }, [clubId, connectWS]);

  // Auto-scroll on new messages if user is at bottom
  useEffect(() => {
    if (!listRef.current) return;
    if (stickToBottomRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, typingUsers]);

  // Mark-read on focus/blur
  useEffect(() => {
    const onFocus = async () => {
      try { await axios.post(`${BACKEND_URL}/api/clubs/${clubId}/chat/read`, {}, { withCredentials: true }); } catch (e) {}
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [clubId]);

  // Clear stale typing indicators
  useEffect(() => {
    const t = setInterval(() => {
      setTypingUsers((prev) => {
        const cutoff = Date.now() - 3500;
        const next = {};
        for (const [k, v] of Object.entries(prev)) if (v > cutoff) next[k] = v;
        return next;
      });
    }, 1500);
    return () => clearInterval(t);
  }, []);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/clubs/${clubId}/chat/messages`,
        { content: text },
        { withCredentials: true }
      );
      // Optimistic add — UI updates instantly even if WS lags. Dedupe later
      // when the broadcast arrives.
      setMessages((prev) => {
        if (prev.some((m) => m.message_id === res.data.message_id)) return prev;
        return [...prev, res.data];
      });
      setInput('');
      setShowEmoji(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleTyping = (val) => {
    setInput(val);
    if (wsRef.current && wsRef.current.readyState === 1) {
      try { wsRef.current.send(JSON.stringify({ type: 'typing' })); } catch (e) {}
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/clubs/${clubId}/chat/messages/${messageId}`, {
        withCredentials: true,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const onScroll = () => {
    if (!listRef.current) return;
    const el = listRef.current;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const isMod = clubCreatorId && currentUser && clubCreatorId === currentUser.user_id;
  const typingCount = Object.keys(typingUsers).length;

  return (
    <div
      className="flex flex-col h-[520px] bg-[#0F0F10] border border-white/10 rounded-2xl overflow-hidden text-white"
      data-testid="club-chat"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-white/[0.03]">
        <div className="flex items-center gap-2">
          <Users2 className="w-4 h-4 text-lime-400" />
          <span className="text-sm font-semibold">Club Chat</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${connected ? 'bg-lime-500/20 text-lime-400' : 'bg-amber-500/20 text-amber-400'}`}>
            {connected ? 'Live' : 'Reconnecting…'}
          </span>
        </div>
        <span className="text-[11px] text-neutral-500">{clubMembers.length || 0} {clubMembers.length === 1 ? 'member' : 'members'}</span>
      </div>

      {/* Message list */}
      <div
        ref={listRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto px-3 py-4 space-y-2"
        data-testid="club-chat-messages"
      >
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-neutral-500" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 text-sm text-neutral-500">
            Be the first to say hi to your circle 👋
          </div>
        ) : (
          messages.map((m) => (
            <MessageRow
              key={m.message_id}
              m={m}
              mine={m.user_id === currentUser?.user_id}
              canDelete={(m.user_id === currentUser?.user_id) || isMod}
              onDelete={() => handleDelete(m.message_id)}
            />
          ))
        )}
        {typingCount > 0 && (
          <div className="text-[11px] text-neutral-500 italic px-2" data-testid="club-chat-typing">
            {typingCount === 1 ? 'Someone is typing…' : `${typingCount} people are typing…`}
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={handleSend} className="border-t border-white/10 px-3 py-3 bg-black/40">
        {showEmoji && (
          <div className="mb-2 flex flex-wrap gap-1" data-testid="club-chat-emoji-picker">
            {EMOJI.map((e) => (
              <button
                type="button"
                key={e}
                onClick={() => setInput((v) => v + e)}
                className="text-xl px-1.5 py-0.5 rounded hover:bg-white/10"
              >
                {e}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowEmoji((v) => !v)}
            className="p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white transition-colors"
            data-testid="club-chat-emoji-toggle"
          >
            <Smile className="w-5 h-5" />
          </button>
          <Input
            value={input}
            onChange={(e) => handleTyping(e.target.value)}
            placeholder="Message your circle…"
            className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-lime-500"
            disabled={sending}
            data-testid="club-chat-input"
            maxLength={2000}
          />
          <Button
            type="submit"
            disabled={!input.trim() || sending}
            className="bg-lime-500 hover:bg-lime-400 text-black font-bold"
            data-testid="club-chat-send-button"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <div className="text-[10px] text-neutral-600 mt-2 flex items-center gap-1">
          <ShieldAlert className="w-3 h-3" />
          Account and card numbers are auto-redacted to protect members.
        </div>
      </form>
    </div>
  );
}

function MessageRow({ m, mine, canDelete, onDelete }) {
  if (m.deleted) {
    return (
      <div className="px-3 py-1 text-[11px] italic text-neutral-600">
        — Message removed —
      </div>
    );
  }
  const time = (() => {
    try { return new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  })();
  return (
    <div className={`flex group ${mine ? 'justify-end' : 'justify-start'}`} data-testid="club-chat-message">
      <div className={`max-w-[78%] md:max-w-[65%] rounded-2xl px-3 py-2 ${mine ? 'bg-lime-500 text-black' : 'bg-white/10 text-white'}`}>
        {!mine && (
          <div className="text-[11px] font-bold opacity-80 mb-0.5">{m.user_name}</div>
        )}
        <div className="text-sm whitespace-pre-wrap break-words">
          {renderWithMentions(m.content)}
          {m.redacted && (
            <span className="ml-1 text-[10px] opacity-70" title="Sensitive numbers were redacted by Susu">🛡</span>
          )}
        </div>
        {m.flagged && (
          <div
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"
            data-testid="club-chat-message-flag"
            title={m.moderation?.reason || 'This message was flagged for review by the club creator.'}
          >
            ⚠ Flagged — pending review
          </div>
        )}
        <div className="text-[10px] opacity-60 mt-0.5 flex items-center justify-between gap-2">
          <span>{time}</span>
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`club-chat-delete-${m.message_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function renderWithMentions(text) {
  const parts = [];
  const re = /@([A-Za-z0-9_]{2,32})/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={i++} className="font-bold text-amber-300 bg-amber-400/10 px-1 rounded">
        @{m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}
